import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('tapInToken'));
  const [loading, setLoading] = useState(true);
  
  // Use a ref to always have the latest token value for interceptors
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Create axios instance with auth header - memoized to prevent recreation
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth header to requests - use ref to always get latest token
    instance.interceptors.request.use((config) => {
      const currentToken = tokenRef.current || localStorage.getItem('tapInToken');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []);

  // Fetch current user on mount - Also check localStorage for user data for persistence
  const fetchUser = useCallback(async () => {
    const currentToken = tokenRef.current || localStorage.getItem('tapInToken');
    
    // If no token, clear any cached user data and set not authenticated
    if (!currentToken) {
      console.log('[Auth] No token found, clearing any cached data');
      localStorage.removeItem('tapInUser');
      setUser(null);
      setLoading(false);
      return;
    }
    
    // First check if we have cached user data for instant UI
    const cachedUser = localStorage.getItem('tapInUser');
    if (cachedUser && currentToken) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        setUser(parsedUser);
        // Set loading to false immediately when we have cached data
        // This prevents the "loop" feeling on mobile
        setLoading(false);
      } catch (e) {
        console.error('[Auth] Invalid cached user data:', e);
        // Invalid cached data, continue to API call
      }
    }

    try {
      console.log('[Auth] Fetching user from API...');
      const response = await api.get('/api/auth/me');
      console.log('[Auth] User fetched successfully:', response.data?.username);
      setUser(response.data);
      // Cache user data for persistence
      localStorage.setItem('tapInUser', JSON.stringify(response.data));
    } catch (error) {
      console.error('[Auth] Failed to fetch user:', error);
      // Only clear tokens on 401 (unauthorized) errors, not on network errors
      if (error?.response?.status === 401) {
        console.log('[Auth] Token invalid (401), clearing credentials');
        localStorage.removeItem('tapInToken');
        localStorage.removeItem('tapInUser');
        setToken(null);
        setUser(null);
      } else {
        console.log('[Auth] Non-401 error, keeping cached user if available');
        // Keep cached user on network errors to prevent logout on bad connection
        const cachedUserData = localStorage.getItem('tapInUser');
        if (cachedUserData) {
          try {
            setUser(JSON.parse(cachedUserData));
          } catch (e) {
            console.error('[Auth] Failed to parse cached user:', e);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [token, fetchUser]);

  // Claim magic link portal access for logged-in user
  const claimMagicLinkAccess = async (userData) => {
    try {
      const magicAccess = sessionStorage.getItem('tapInMagicAccess');
      if (magicAccess) {
        const { token } = JSON.parse(magicAccess);
        if (token && userData?.id) {
          const response = await api.post(`/api/admin/public/magic-link/${token}/claim?user_id=${userData.id}`);
          if (response.data?.success) {
            console.log('[Auth] Magic link access claimed:', response.data.portals_granted);
            // Clear the magic access from session storage after claiming
            sessionStorage.removeItem('tapInMagicAccess');
          }
        }
      }
    } catch (err) {
      console.error('[Auth] Failed to claim magic link access:', err);
      // Don't throw - this shouldn't block login
    }
  };

  const signup = async (email, password, displayName) => {
    const response = await api.post('/api/auth/signup', {
      email,
      password,
      display_name: displayName,
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('tapInToken', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Claim any magic link access after successful signup
    await claimMagicLinkAccess(userData);
    
    return userData;
  };

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', null, {
      params: { email, password }
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('tapInToken', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Claim any magic link access after successful login
    await claimMagicLinkAccess(userData);
    
    return userData;
  };

  const sendPhoneCode = async (phoneNumber) => {
    const response = await api.post('/api/auth/phone/send-code', {
      phone_number: phoneNumber,
    });
    return response.data;
  };

  const verifyPhoneCode = async (phoneNumber, code) => {
    const response = await api.post('/api/auth/phone/verify', {
      phone_number: phoneNumber,
      code,
    });
    
    const { access_token, user: userData } = response.data;
    
    // Clear ALL stale data from previous user's session
    localStorage.removeItem('tapInActiveSession');
    localStorage.removeItem('tapInPlaylists'); // CRITICAL: Clear cached playlists to prevent cross-user data
    
    localStorage.setItem('tapInToken', access_token);
    localStorage.setItem('tapInUser', JSON.stringify(userData)); // Cache user data
    setToken(access_token);
    setUser(userData);
    
    // Claim any magic link access after successful login
    await claimMagicLinkAccess(userData);
    
    return userData;
  };

  const checkUsername = async (username) => {
    const response = await api.post('/api/users/check-username', { username });
    return response.data;
  };

  const updateUsername = async (username) => {
    const response = await api.put('/api/users/username', { username });
    setUser(response.data);
    return response.data;
  };

  const updateOnboarding = async (data) => {
    const response = await api.put('/api/users/onboarding', data);
    setUser(response.data);
    return response.data;
  };

  const completeOnboarding = async () => {
    const response = await api.post('/api/users/complete-onboarding');
    setUser(response.data);
    return response.data;
  };

  const addPhoneNumber = async (phoneNumber) => {
    const response = await api.put('/api/users/phone', null, {
      params: { phone_number: phoneNumber }
    });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    // End any active sessions before logging out
    try {
      const currentToken = localStorage.getItem('tapInToken');
      if (currentToken) {
        // Get active sessions
        const response = await fetch(`${API_URL}/api/sessions/active`, {
          headers: { 'Authorization': `Bearer ${currentToken}` }
        });
        if (response.ok) {
          const sessions = await response.json();
          // Find session hosted by current user
          const userHostedSession = sessions.find(s => s.host_id === user?.id);
          if (userHostedSession) {
            // End the session
            await fetch(`${API_URL}/api/sessions/${userHostedSession.id}/end`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${currentToken}` }
            });
            console.log('[Auth] Ended active session on logout');
          }
        }
      }
    } catch (err) {
      console.error('[Auth] Error ending session on logout:', err);
    }
    
    localStorage.removeItem('tapInToken');
    localStorage.removeItem('tapInUser');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
      localStorage.setItem('tapInUser', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isOnboarded: user?.is_onboarded ?? false,
    api,
    signup,
    login,
    sendPhoneCode,
    verifyPhoneCode,
    checkUsername,
    updateUsername,
    updateOnboarding,
    completeOnboarding,
    addPhoneNumber,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
